import { useState } from "react";
import { useDrag } from "react-dnd";
import { BINARY_SYSTEM, COLOR_MEDIUMDARK, MAYA_NUMBER_OF_LEVELS, MAYA_SYSTEM, ROMAN_SYSTEM } from "../defaults";
import basket from "../images/basket.png";
import cart from "../images/cart.png";
import coin_C from "../images/coin_C.png";
import coin_D from "../images/coin_D.png";
import coin_I from "../images/coin_I.png";
import coin_L from "../images/coin_L.png";
import coin_M from "../images/coin_M.png";
import coin_V from "../images/coin_V.png";
import coin_X from "../images/coin_X.png";
import corn from "../images/corn.png";
import { buttonChangeSystem, buttonShowLegend, levelUnlockedBody1, levelUnlockedBody2, levelUnlockedBodyCompletedGame1, levelUnlockedBodyCompletedGame2, levelUnlockedBodyCompletedGame3, levelUnlockedBodyLastLevel1, levelUnlockedBodyLastLevel2, levelUnlockedBodyLastLevel3, levelUnlockedBodyOneExercise, levelUnlockedContinue, levelUnlockedStay, levelUnlockedTitle } from "../strings";
import Tutorial from "../tutorial";

// Define Item Type for Drag and Drop
const ItemTypes = {
    MESSAGE: 'message',
    LEGEND: 'legend',
    TUTORIAL: 'tutorial',
    LEVEL_UNLOCKED: 'level_unlocked'
};

/**
 * Partially LLM-generated.
 * @param {Boolean} show If true, popup is visible. If false, popup is hidden.
 * @param {*} setShow Setter function for show parameter.
 * @param {*} handleClose External function that will be called upon closing the popup.
 * @param {String} message Message text.
 * @param {String} fontSize Font size in dvh of message.
 * @returns A draggable message popup.
 */
export function DraggableMessage({ show, setShow, handleClose, message, fontSize }) {

  const hidePopup = () => {
    setShow(false);
    handleClose();
  };
  
  const [position, setPosition] = useState(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.MESSAGE,
    item: (monitor) => { // Define item as a function to access monitor
        const initialClientOffset = monitor.getInitialClientOffset();
        const initialSourceClientOffset = monitor.getInitialSourceClientOffset();
        // Calculate the offset of the pointer relative to the item's top-left corner. This is necessary so the dropping position feels natural.
        const offset = initialClientOffset && initialSourceClientOffset ? {
            x: initialClientOffset.x - initialSourceClientOffset.x,
            y: initialClientOffset.y - initialSourceClientOffset.y,
        } : { x: 0, y: 0 }; // Default offset if values aren't available yet

        return { type: ItemTypes.MESSAGE, offset }; // Include offset in the item
    },
    end: (item, monitor) => {
      const finalClientOffset = monitor.getClientOffset(); // Get final cursor position
      if (finalClientOffset && item.offset) {
        // Calculate the correct top-left position by subtracting the initial offset
        const finalPosition = {
          x: finalClientOffset.x - item.offset.x,
          y: finalClientOffset.y - item.offset.y,
        };
        setPosition(finalPosition); // Update state with the adjusted position
      }
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
      // No need to collect currentOffset here anymore
    }),
  }), [setPosition]); // Add setPosition dependency

  // Style for positioning the element using state
  const popupStyle = {
    position: 'fixed', // Use fixed positioning
    // Use stored position if available, otherwise use CSS defaults (remove explicit defaults)
    left: position ? `${position.x}px` : undefined,
    top: position ? `${position.y}px` : undefined,
    opacity: isDragging ? 0.2 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    fontSize: fontSize ? fontSize : "5dvh",
    display: show ? "flex" : "none"
  };

  const bodyStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: fontSize ? fontSize : "5dvh",
    height: "75%",
    width: "100%",
    border: "6px solid rgba(255, 255, 255, 0.15)", /* LLM-generated */
    borderRadius: "0.25em",
    boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.1)", /* LLM-generated */
    backgroundColor: COLOR_MEDIUMDARK,
    padding: "4dvh",
    boxSizing: "border-box"
  };


  return (
    <div ref={drag} className="checkAnswer-message" style={popupStyle}>
      <button
      className="x-button"
      onClick={hidePopup}
      >
        <span style={{lineHeight: "4dvh", position: "absolute", top: "-0.55dvh", right: "0"}}>&#x00d7;</span>
      </button>
      <div style={bodyStyle}>{message}</div>
      
    </div>
  );
}



/**
 * Partially LLM-generated.
 * @param {Boolean} show If true, popup is visible. If false, popup is hidden.
 * @param {} setShow Setter function for show parameter.
 * @param {Number} whichSystem Which numeral system to show the legend for.
 * @param {Number} currentLevel Which level to show the legend for.
 * @returns A draggable legend popup.
 */
export function DraggableLegend({ show, setShow, whichSystem, currentLevel }) {

  const hidePopup = () => {
    setShow(false);
  };

  const isLastLevel = (currentLevel === MAYA_NUMBER_OF_LEVELS-1); // Relevant for Maya only.
  
  const [position, setPosition] = useState(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.LEGEND,
    item: (monitor) => { // Define item as a function to access monitor
        const initialClientOffset = monitor.getInitialClientOffset();
        const initialSourceClientOffset = monitor.getInitialSourceClientOffset();
        // Calculate the offset of the pointer relative to the item's top-left corner. This is necessary so the dropping position feels natural.
        const offset = initialClientOffset && initialSourceClientOffset ? {
            x: initialClientOffset.x - initialSourceClientOffset.x,
            y: initialClientOffset.y - initialSourceClientOffset.y,
        } : { x: 0, y: 0 }; // Default offset if values aren't available yet

        return { type: ItemTypes.LEGEND, offset }; // Include offset in the item
    },
    end: (item, monitor) => {
      const finalClientOffset = monitor.getClientOffset(); // Get final cursor position
      if (finalClientOffset && item.offset) {
        // Calculate the correct top-left position by subtracting the initial offset
        const finalPosition = {
          x: finalClientOffset.x - item.offset.x,
          y: finalClientOffset.y - item.offset.y,
        };
        setPosition(finalPosition); // Update state with the adjusted position
      }
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
      // No need to collect currentOffset here anymore
    }),
  }), [setPosition]); // Add setPosition dependency

  // Style for positioning the element using state
  const popupStyle = {
    position: 'fixed', // Use fixed positioning
    // Use stored position if available, otherwise use CSS defaults (remove explicit defaults)
    left: position ? `${position.x}px` : undefined,
    top: position ? `${position.y}px` : undefined,
    opacity: isDragging ? 0.2 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    fontSize: "5dvh",
    display: show ? "flex" : "none",
  };

  const legendStyle = {
    display: "grid",
    gridTemplateRows: (whichSystem === ROMAN_SYSTEM) ? "1fr 1fr 1fr 1fr 1fr 1fr 1fr" : isLastLevel ? "1fr 1fr 4fr" : "1fr 1fr", // Number of rows depends on system and whether or not it is the last Maya level.
    gridTemplateColumns: "repeat(3, max-content)",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "6dvh",
    columnGap: "2dvh",
    fontSize: "3.5dvh",
    textShadow:"0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    padding: "2dvh",
    borderRadius: "0.25em",
    border: "6px solid rgba(255, 255, 255, 0.15)", /* LLM-generated */
    boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.1)", /* LLM-generated */
    backgroundColor: COLOR_MEDIUMDARK
  };

  const cellStyle = {
    display: "flex",
  };

  const coinStyle = {
    width: "5dvh",
    height: "5dvh",
    filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))",
  };

  const mayaStyle = {
    width: "4dvh",
    height: "4dvh",
    filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))",
  };

  const titleStyle = {
    fontSize: "2.5dvh",
    textShadow:"0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    paddingTop: "0.5dvh",
    paddingBottom: "2dvh"
  };

  return (
    <div ref={drag} className="legend" style={popupStyle}>
      <button
      className="x-button"
      onClick={hidePopup}
      >
        <span style={{lineHeight: "4dvh", position: "absolute", top: "-0.55dvh", right: "0"}}>&#x00d7;</span>
      </button>
      {whichSystem === ROMAN_SYSTEM && 
      <div>
        <div style={titleStyle}>{buttonShowLegend}</div>
        <div style={legendStyle}>
          <div style={cellStyle}><img alt="coin" src={coin_I} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div> {/* Different font, because equal sign with default font is not properly vertically centered. */}
          <div style={{...cellStyle, marginLeft: "0.6dvh"}}>1</div>

          <div style={cellStyle}><img alt="coin" src={coin_V} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 5}).map(() => (<img alt="coin" src={coin_I} style={coinStyle}/>))}</div>

          <div style={cellStyle}><img alt="coin" src={coin_X} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 2}).map(() => (<img alt="coin" src={coin_V} style={coinStyle}/>))}</div>

          <div style={cellStyle}><img alt="coin" src={coin_L} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 5}).map(() => (<img alt="coin" src={coin_X} style={coinStyle}/>))}</div>

          <div style={cellStyle}><img alt="coin" src={coin_C} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 2}).map(() => (<img alt="coin" src={coin_L} style={coinStyle}/>))}</div>

          <div style={cellStyle}><img alt="coin" src={coin_D} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 5}).map(() => (<img alt="coin" src={coin_C} style={coinStyle}/>))}</div>

          <div style={cellStyle}><img alt="coin" src={coin_M} style={coinStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 2}).map(() => (<img alt="coin" src={coin_D} style={coinStyle}/>))}</div>
        </div>
      </div>
      }
      {whichSystem === MAYA_SYSTEM && 
      <div>
        <div style={titleStyle}>{buttonShowLegend}</div>
        <div style={legendStyle}>
          <div style={{...cellStyle, justifyContent: "center"}}><img alt="maya" src={corn} style={mayaStyle}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div> {/* Different font, because equal sign with default font is not properly vertically centered. */}
          <div style={{...cellStyle, justifyContent: "center"}}>1</div>

          <div style={cellStyle}><img alt="maya" src={basket} style={{...mayaStyle, width: "16dvh"}}/></div> 
          <div style={{...cellStyle, fontFamily: "Arial"}}>=</div>
          <div style={cellStyle}>{Array.from({length: 5}).map(() => (<img alt="maya" src={corn} style={mayaStyle}/>))}</div>

          <div style={{...cellStyle, display: isLastLevel ? "flex" : "none"}}><img alt="maya" src={cart} style={{...mayaStyle, width: "16dvh"}}/></div> 
          <div style={{...cellStyle, display: isLastLevel ? "flex" : "none", fontFamily: "Arial"}}>=</div>
          <div style={{...cellStyle, display: isLastLevel ? "flex" : "none", flexDirection: "column", rowGap: "1dvh", alignItems: "center"}}>{Array.from({length: 4}).map(() => (<img alt="maya" src={basket} style={{...mayaStyle, width: "16dvh"}}/>))}</div>
        </div>
      </div>
      }
    </div>
  );
}



/**
 * Partially LLM-generated.
 * @param {Boolean} show If true, popup is visible. If false, popup is hidden.
 * @param {} setShow Setter function for show parameter.
 * @param {Number} whichSystem Which numeral system to show the tutorial for.
 * @param {Number} currentLevel Which level to show the tutorial for.
 * @returns A draggable tutorial popup.
 */
export function DraggableTutorial({ show, setShow, whichSystem, currentLevel, tutorialProgress, setTutorialProgress }) {

  // Mark tutorial for currentLevel as completed.
  const tutorialDone = (currentLevel) => {
    const newProgress = [...tutorialProgress(whichSystem)];
    newProgress[currentLevel] = 1;
    setTutorialProgress(whichSystem, newProgress);
  };

  // Hide popup and mark tutorial as completed.
  const hidePopup = () => {
    setShow(false);
    tutorialDone(currentLevel);
  };

  const isTableDivision = (whichSystem === ROMAN_SYSTEM && currentLevel === 5) || (whichSystem === BINARY_SYSTEM && currentLevel === 7); // Check if level is table division.
  
  const [position, setPosition] = useState(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TUTORIAL,
    item: (monitor) => { // Define item as a function to access monitor
        const initialClientOffset = monitor.getInitialClientOffset();
        const initialSourceClientOffset = monitor.getInitialSourceClientOffset();
        // Calculate the offset of the pointer relative to the item's top-left corner. This is necessary so the dropping position feels natural.
        const offset = initialClientOffset && initialSourceClientOffset ? {
            x: initialClientOffset.x - initialSourceClientOffset.x,
            y: initialClientOffset.y - initialSourceClientOffset.y,
        } : { x: 0, y: 0 }; // Default offset if values aren't available yet

        return { type: ItemTypes.TUTORIAL, offset }; // Include offset in the item
    },
    end: (item, monitor) => {
      const finalClientOffset = monitor.getClientOffset(); // Get final cursor position
      if (finalClientOffset && item.offset) {
        // Calculate the correct top-left position by subtracting the initial offset
        const finalPosition = {
          x: finalClientOffset.x - item.offset.x,
          y: finalClientOffset.y - item.offset.y,
        };
        setPosition(finalPosition); // Update state with the adjusted position
      }
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
      // No need to collect currentOffset here anymore
    }),
  }), [setPosition]); // Add setPosition dependency

  // Style for positioning the element using state
  const popupStyle = {
    position: 'fixed', // Use fixed positioning
    // Use stored position if available, otherwise use CSS defaults (remove explicit defaults)
    left: position ? `${position.x}px` : undefined,
    top: position ? `${position.y}px` : isTableDivision ? "14.5%" : undefined, // Table division tutorials need more space.
    opacity: isDragging ? 0.2 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    display: show ? "flex" : "none",
    height: isTableDivision ? "65dvh" : undefined // Table division tutorials need more space.
  };

  return (
    <div ref={drag} className="tutorial-wrapper" style={popupStyle}>
      <button
      className="x-button"
      onClick={hidePopup}
      >
        <span style={{lineHeight: "4dvh", position: "absolute", top: "-0.55dvh", right: "0"}}>&#x00d7;</span>
      </button>
      <Tutorial whichSystem={whichSystem} level={currentLevel} finishTutorial={hidePopup}/>
    </div>
  );
}



/**
 * Partially LLM-generated.
 * @param {Boolean} show If true, popup is visible. If false, popup is hidden.
 * @param {} setShow Setter function for show parameter.
 * @param {Number} whichSystem Used to load the appropriate screen.
 * @param {Number} currentLevel Used to load the appropriate screen.
 * @returns A draggable popup for when a player unlocks a new level.
 */
export function DraggableLevelUnlocked({ show, setShow, isLastLevel, isOneExercise, hasCompletedGame, setLevel, changeSystem}) {

  // Hide popup.
  const hidePopup = () => {
    setShow(false);
  };

  // Jump to next level.
  const handleContinue = () => {
    hidePopup();
    setLevel(n => n + 1);
  };

  // Display ChangeSystem popup.
  const handleChangeSystem = () => {
    hidePopup();
    changeSystem();
  };

  
  const [position, setPosition] = useState(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.LEVEL_UNLOCKED,
    item: (monitor) => { // Define item as a function to access monitor
        const initialClientOffset = monitor.getInitialClientOffset();
        const initialSourceClientOffset = monitor.getInitialSourceClientOffset();
        // Calculate the offset of the pointer relative to the item's top-left corner. This is necessary so the dropping position feels natural.
        const offset = initialClientOffset && initialSourceClientOffset ? {
            x: initialClientOffset.x - initialSourceClientOffset.x,
            y: initialClientOffset.y - initialSourceClientOffset.y,
        } : { x: 0, y: 0 }; // Default offset if values aren't available yet

        return { type: ItemTypes.LEVEL_UNLOCKED, offset }; // Include offset in the item
    },
    end: (item, monitor) => {
      const finalClientOffset = monitor.getClientOffset(); // Get final cursor position
      if (finalClientOffset && item.offset) {
        // Calculate the correct top-left position by subtracting the initial offset
        const finalPosition = {
          x: finalClientOffset.x - item.offset.x,
          y: finalClientOffset.y - item.offset.y,
        };
        setPosition(finalPosition); // Update state with the adjusted position
      }
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
      // No need to collect currentOffset here anymore
    }),
  }), [setPosition]); // Add setPosition dependency

  // Style for positioning the element using state
  const popupStyle = {
    position: 'fixed', // Use fixed positioning
    // Use stored position if available, otherwise use CSS defaults (remove explicit defaults)
    left: position ? `${position.x}px` : undefined,
    top: position ? `${position.y}px` : undefined,
    opacity: isDragging ? 0.2 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    display: show ? "flex" : "none",
  };

  return (
    <div ref={drag} className="level-unlocked-wrapper" style={popupStyle}>
      {!isOneExercise && // Do not render the x-button if is a one-exercise level.
      <button
      className="x-button"
      onClick={hidePopup}
      >
        <span style={{lineHeight: "4dvh", position: "absolute", top: "-0.55dvh", right: "0"}}>&#x00d7;</span>
      </button>}
      <div className="level-unlocked-title">
        {levelUnlockedTitle}
      </div>
      <div className="level-unlocked-body">
        {/* Not last level */}
        {!isLastLevel && levelUnlockedBody1}
        {!isLastLevel && <div><br></br></div>}
        {!isLastLevel && !isOneExercise && levelUnlockedBody2} {/* Normal level. */}
        {!isLastLevel && isOneExercise && levelUnlockedBodyOneExercise} {/* One-exercise level. */}

        {/* Last level */}
        {isLastLevel && !hasCompletedGame && levelUnlockedBodyLastLevel1} {/* Did it the ugly way since I could not figure out how to chain these statements without breaking them. */}
        {isLastLevel && !hasCompletedGame && (<div><br></br></div>)}
        {isLastLevel && !hasCompletedGame && levelUnlockedBodyLastLevel2}
        {isLastLevel && !hasCompletedGame && (<div><br></br></div>)}
        {isLastLevel && !hasCompletedGame && levelUnlockedBodyLastLevel3}

        {isLastLevel && hasCompletedGame && levelUnlockedBodyCompletedGame1}
        {isLastLevel && hasCompletedGame && (<div><br></br></div>)}
        {isLastLevel && hasCompletedGame && levelUnlockedBodyCompletedGame2}
        {isLastLevel && hasCompletedGame && (<div><br></br></div>)}
        {isLastLevel && hasCompletedGame && levelUnlockedBodyCompletedGame3}

      </div>

      {/* Button to jump to next level. Only renders if a next level exists. */}
      {!isLastLevel &&
        <button
        onClick={handleContinue}
          className="level-unlocked-continue-button"
        >
        {levelUnlockedContinue}
        </button>
      }

      {/* Button to stay in current level. Only renders if not a level with just one exercise. */}
      {!isOneExercise &&
        <button
          onClick={hidePopup}
          className="level-unlocked-stay-button"
        >
        {levelUnlockedStay}
        </button>
      }

      {/* Unimplemented Easter Egg. */}
      {/* {isLastLevel && hasCompletedGame &&
        <button
        onClick={handleGameComplete}
          className="level-unlocked-continue-button"
        >
        {levelUnlockedEasterEgg}
        </button>
      } */}

      {isLastLevel &&
        <button
        onClick={handleChangeSystem}
          className="level-unlocked-continue-button"
          style={{fontSize: "2dvh"}}
        >
        {buttonChangeSystem}
        </button>
      }
    </div>
  );
}